import * as R from 'ramda'
import moment from 'moment'
import breakdown from './breakdown'
import { potentialRentProjected } from './potentialRent'
import { parsePeriod } from 'utils/stuff'

let actual = (doc, period) => (
	moment(doc.date || doc.dateFrom) >= moment(period.from) && moment(doc.date || doc.dateFrom) < moment(period.to)
	||
	moment(doc.dateTill) >= moment(period.from) && moment(doc.dateTill) < moment(period.to)
	||
	moment(doc.date || doc.dateFrom) >= moment(period.from) && moment(doc.dateTill) < moment(period.to)
	||
	moment(doc.date || doc.dateFrom) <= moment(period.from) && moment(doc.dateTill) >= moment(period.to)
)

let calculateRent = (monthlyRent, period) => {
	let intervals = []
	let currIntervalEnd = moment(period.from).endOf('month')
	let currIntervalStart = moment(period.from)
	while(currIntervalEnd <= moment(period.to).endOf('day')){
		intervals.push({firstDay: currIntervalStart.format('YYYY-MM-DD'), lastDay:currIntervalEnd.format('YYYY-MM-DD')})
		currIntervalEnd.add(1, 'month').endOf('month')
		currIntervalStart.add(1, 'month').startOf('month')
	}
	let rent = 0
	for(let i = 0; i < intervals.length; i++){
		let interval = intervals[i]
		let fullDays = moment(interval.lastDay).diff(moment(interval.firstDay), 'days') + 1
		let currMonth = moment(period.from).add(i, 'month')
		rent += monthlyRent / currMonth.daysInMonth() * fullDays
	}
	return rent
}

export default (transactions, properties, leases, listings, startMarketDateByPropertyID, period) => {
	let parsedPeriod = period == 'projected' ? parsePeriod(moment().year()) : parsePeriod(period)
	if(period == 'projected'){
		var projectedStatement = incomeStatementProjected(leases, listings, properties, startMarketDateByPropertyID, period)
	}
	transactions = R.filter(x => parsedPeriod.from <= x.dueDate && parsedPeriod.to > x.dueDate)(transactions)
	let invoicesByPropertyID = R.pipe(
		R.values
	  , R.filter(x => x.type == 'transaction/invoice')
	  , R.groupBy(R.prop('propertyID'))
	)(transactions)
	let billsByPropertyID = R.pipe(
		R.values
	  , R.filter(x => x.type == 'transaction/bill')
	  , R.groupBy(R.prop('propertyID'))
	)(transactions)

	let calcRentIncome = R.reduce((sum, invoice) => sum + (invoice.amount - invoice.balance), 0)
	let calcPendingRent = R.reduce((sum, invoice) => sum + invoice.balance, 0)
	let aggregate = R.pipe(R.values, R.reduce(R.add, 0))

	let rentIncome = R.map(property => {
		return calcRentIncome(invoicesByPropertyID[property._id] || [])
	})(properties)
	rentIncome.total = aggregate(rentIncome)
	if(period == 'projected') {
		rentIncome = R.mergeWith(R.add, rentIncome, projectedStatement.rentIncome)
	}
	let pendingRent = R.map(property => {
		let invoices = R.filter(invoice => moment(invoice.date) < moment().startOf('month'))(invoicesByPropertyID[property._id] || [])
		return calcPendingRent(invoices || [])
	})(properties)
	pendingRent.total = aggregate(pendingRent)

	let expenses = R.map((property) => {
		let propertyBills = billsByPropertyID[property._id] || []
		let billsByCategory = R.pipe(R.map(x => x.expenses), R.reduce((acc, item) => acc.concat(item), []), R.groupBy(x => x.category.split(":")[1].split(" ")[1]))(propertyBills)
		let categories = R.map(categoryBills => {
			return R.reduce((sum, next) => sum + +next.amount, 0)(categoryBills)
		})(billsByCategory)
		let total = R.pipe(R.values, R.reduce(R.add, 0))(categories)
		return {categories, total}
	})(properties)
	let total = R.pipe(R.values, R.reduce((total, stats) => R.mergeWith(R.add, total, stats.categories), {}))(expenses || {})
	if(period == 'projected') {
		expenses = R.mergeDeepWith(R.add, expenses, projectedStatement.expenses)
	}
	let sortExpenses = R.pipe(R.mapObjIndexed((amount, category) => ({amount, category})), R.values, R.sortBy(R.prop('category')))
	expenses = {
		...R.map((stats) => ({
			...stats
		  , categories: sortExpenses(stats.categories)
		}))(expenses)
	  , total: {
			categories: sortExpenses(total)
		  , total: aggregate(total)
		}
	}
	let netIncome = R.map(property => (rentIncome[property._id] || 0) - (expenses[property._id] || {total: 0}).total)(properties)
	netIncome.total = aggregate(netIncome)
	return ({
		rentIncome
	  , expenses
	  , netIncome
	  , pendingRent
	})
}

export let incomeStatementProjected = (leases, listings, properties, startMarketDateByPropertyID, period) => {
	let parsedPeriod = parsePeriod(period)
	let termsByParentID = R.pipe(
		R.values
	  , R.filter(x => x.parentID)
	  , R.groupBy(R.prop('parentID'))
	  , R.map(terms => R.sortBy(x => moment(x.dateTill))(terms))
	)(leases)
	let leasesByPropertyID = R.pipe(
		R.values
	  , R.filter(x => !x.parentID)
	  , R.map(x => {x.dateTill = termsByParentID[x._id] ? termsByParentID[x._id][termsByParentID[x._id].length-1].dateTill : x.dateTill; return x;})
	  , R.filter(x => actual(x, parsedPeriod))
	  , R.groupBy(R.prop('propertyID'))
	  , R.map(propertyLeases => R.pipe(R.sortBy(x => moment(x.dateTill)), R.reverse)(propertyLeases))
	)(leases)
	let listingsByPropertyID = R.pipe(
		R.values
	  , R.filter(x => actual(x, parsedPeriod))
	  , R.groupBy(R.prop('propertyID'))
	  , R.map(propertyListings => R.pipe(R.sortBy(x => moment(x.dateTill)), R.reverse)(propertyListings))
	)(listings)

	let rentIncome = rentIncomeProjected(leasesByPropertyID, listingsByPropertyID, properties, period, )
	let expenses = expensesProjected(leasesByPropertyID, listingsByPropertyID, properties, startMarketDateByPropertyID, period, leases, listings)

	return ({
		rentIncome
	  , expenses
	})
}

export let rentIncomeProjected = (leasesByPropertyID, listingsByPropertyID, properties, period) => {
	let parsedPeriod = parsePeriod(period)

	let res = R.map(property => {
		let rentCollected = 0
		let daysForRent = 0

		let activeLease = R.find(lease => moment(lease.date) <= moment(parsePeriod.from) && moment(lease.breakDate || lease.dateTill) >= moment(parsePeriod.from))(leasesByPropertyID[property._id] || [])
		let futureLease = R.find(lease => moment(lease.date) > moment(parsePeriod.from) && moment(lease.date) <= moment(parsePeriod.to))(leasesByPropertyID[property._id] || [])

		if(activeLease){
			rentCollected += calculateRent(activeLease.rent, {from: parsedPeriod.from, to: moment(activeLease.breakDate || activeLease.dateTill) > moment(parsedPeriod.to) ? parsedPeriod.to : activeLease.dateTill})
		}
		if(futureLease){
			rentCollected += calculateRent(futureLease.rent, {from: activeLease.dateFrom, to: parsedPeriod.to})
		} else {
			let lastListing = listingsByPropertyID[property._id] && listingsByPropertyID[property._id][0]
			let lastLease = leasesByPropertyID[property._id] && leasesByPropertyID[property._id][0]
			let lastRent = lastListing ? lastListing.rent : lastLease ? lastLease.rent : 1000
			let occupiedPeriod = {
				from: moment(lastListing && lastListing.dateFrom || lastLease && lastLease.dateTill).add(30, 'days').format('YYYY-MM-DD')
			  , to: parsedPeriod.to
			}
			rentCollected += calculateRent(lastRent, occupiedPeriod)
		}
		return rentCollected
	})(properties)
	res.total = R.pipe(R.values, R.reduce(R.add, 0))(res)
	return res
}

export let expensesProjected = (leasesByPropertyID, listingsByPropertyID, properties, startMarketDateByPropertyID, period, leases, listings) => {
	let parsedPeriod = parsePeriod(period)
	let breakdownByPropertyID = breakdown(properties, parsedPeriod.to)
	let potentialRentProjectedByPropertyID = potentialRentProjected(leases, listings, properties, startMarketDateByPropertyID, period)
	let res = R.map(property => {
		let categories = {}
		let activeLease = R.find(lease => moment(lease.date) <= moment(parsePeriod.from) && moment(lease.dateTill) >= moment(parsePeriod.from))(leasesByPropertyID[property._id] || [])
		let futureLease = R.find(lease => moment(lease.date) > moment(parsePeriod.from) && moment(lease.date) <= moment(parsePeriod.to))(leasesByPropertyID[property._id] || [])

		let managementAmount = 0
		if(futureLease){
			managementAmount = futureLease.rent
		} else if(!activeLease){
			let lastListing = listingsByPropertyID[property._id] && listingsByPropertyID[property._id][0]
			let lastLease = leasesByPropertyID[property._id] && leasesByPropertyID[property._id][0]
			managementAmount = lastListing ? lastListing.rent : lastLease ? lastLease.rent : 0
		}

		let fullMonths = moment(parsedPeriod.to).diff(parsedPeriod.from, 'months') + 1
		let payment = breakdownByPropertyID[property._id].payment
		let mortgageAmount = payment * fullMonths


		let fullyRent = potentialRentProjectedByPropertyID[property._id]
		let repairAmount = 10 / 100 * fullyRent

		categories['Management'] = managementAmount
		categories['Mortgage'] = mortgageAmount
		categories['Repair'] = repairAmount
		let total = R.pipe(R.values, R.reduce(R.add, 0))(categories)

		return {categories, total}
	})(properties)

	res.total = R.pipe(R.values, R.reduce((total, stats) => R.mergeWith(R.add, total, stats.categories), {}))(res)

	return res
}



// WEBPACK FOOTER //
// ./src/helpers/kpi/incomeStatement.js